// adapters/MapLibreAdapter.ts
import maplibregl, { type StyleSpecification } from "maplibre-gl";
import type { MapAdapter } from "../core/MapAdapter";
import "maplibre-gl/dist/maplibre-gl.css";
import type { MapViewState } from "../types/MapTypes";
import type { MapLayer } from "../layers/types";
import { createMapLibreLayer, expandUrl } from "../layers/helpers/maplibre";
import { AOI_LAYER_ID, MAPLIBRE_BEFORE_ID, PREFIX_MAPLIBRE_LAYER, PREFIX_MAPLIBRE_SOURCE } from "../plugins/aoi/consts";
import * as pmtiles from "pmtiles"
import { cogProtocol } from "@geomatico/maplibre-cog-protocol";
import { changeMousePosition, setView } from "../../../../store/reducers/mapBaseSlice";
import { store } from "../../../../store/store";
import { LABEL_CORECTION } from "../utils/consts";
const protocol = new pmtiles.Protocol();
export class MapLibreAdapter implements MapAdapter
{
  private map?: maplibregl.Map;
  private addedIds?: Set<string>; // list ids of layer added
  private urlCache = new Map<string, string>(); // tracks url per layer id
  private basemapSourceIds: Set<string> = new Set();
  private basemapLayerIds: Set<string> = new Set();

  init(container: HTMLDivElement)
  {
    maplibregl.addProtocol("pmtiles", protocol.tile);
    maplibregl.addProtocol("cog", cogProtocol);
    this.map = new maplibregl.Map({
      container,
      //style: "https://demotiles.maplibre.org/style.json",
      center: [0, 0],
      zoom: 2,
      attributionControl: false,
      renderWorldCopies: false
    });
    const dispatch = store.dispatch;
    const _this = this;

    const dispatchView = () =>
    {
      const c = _this.map!.getCenter();
      dispatch(
        setView({
          center: [c.lng, c.lat],
          zoom: _this.map!.getZoom(),
          bearing: _this.map!.getBearing(),
          pitch: _this.map!.getPitch(),
        }),
      );
    };

    this.map.on("mousemove", function (evt)
    {
      dispatchView();
      dispatch(changeMousePosition([evt?.lngLat?.lng, evt?.lngLat?.lat]));
    });
    this.map.on("zoom", dispatchView);
    // Keep bearing/pitch in state so the compass control reflects rotation.
    this.map.on("rotate", dispatchView);
    this.map.on("pitch", dispatchView);

    this.addedIds = new Set<string>();
  }

  setBasemap(cfg: MapLayer | any | undefined)
  {
    if (!this.map) return;

    if (!cfg)
    {// @ts-ignore
      for (const lid of this.basemapLayerIds)
      {
        try
        {
          if (this.map.getLayer(lid)) this.map.removeLayer(lid);
        } catch { }
      }
      // @ts-ignore
      for (const sid of this.basemapSourceIds)
      {
        try
        {
          if (this.map.getSource(sid)) this.map.removeSource(sid);
        } catch { }
      }
      this.basemapLayerIds.clear();
      this.basemapSourceIds.clear();
      return;
    }

    const style: StyleSpecification | any = {
      id: cfg?.id,
      // MapLibre rejects `name: undefined` ("name: string expected"). Fall back
      // to title/id so basemaps coming from the API (which use `title`) are valid.
      name: cfg?.name ?? cfg?.title ?? cfg?.id ?? "basemap",
      type: cfg?.name || "raster",
      // MapLibre requires the NUMBER 8 (not "8"). API basemaps omit version.
      version: Number(cfg?.["version"]) || 8,
      sources: cfg?.["sources"] || {},
      layers: cfg?.["layers"] || [],
      glyphs: cfg?.["glyphs"],
      sprite: cfg?.["sprite"],
      visibility: true,
    };

    if (!!LABEL_CORECTION && Array.isArray(style?.layers))
    {
      style.layers = style.layers.map((layer: any) =>
      {
        if (layer.type === "symbol" && layer.layout && !!layer?.layout["text-field"])
        {
          return {
            ...layer,
            layout: { ...layer.layout, "text-field": LABEL_CORECTION },
          };
        }
        return layer;
      });
    }

    // Snapshot ids of the OLD basemap so transformStyle can tell basemap vs custom.
    const prevBasemapSrc = new Set(this.basemapSourceIds);
    const prevBasemapLyr = new Set(this.basemapLayerIds);

    this.map.setStyle(style, {
      diff: false,
      transformStyle: (previous, next) =>
      {
        if (!previous) return next as StyleSpecification;

        // Preserve sources not belonging to the old basemap (syncLayers + plugin sources).
        const customSources: Record<string, any> = {};
        for (const [sid, src] of Object.entries(previous.sources || {}))
        {
          if (!prevBasemapSrc.has(sid)) customSources[sid] = src;
        }

        // Preserve layers not belonging to the old basemap, placed on top of the new basemap.
        const customLayers = (previous.layers || []).filter((l: any) => !prevBasemapLyr.has(l.id));

        return {
          ...next,
          sources: { ...(next.sources || {}), ...customSources },
          layers: [...(next.layers || []), ...customLayers],
        } as StyleSpecification;
      },
    });

    // Track ids owned by the NEW basemap so the next swap knows what to drop.
    this.basemapSourceIds = new Set(Object.keys(style.sources || {}));
    this.basemapLayerIds = new Set((style.layers || []).map((l: any) => l.id));
  }

  setCenter(lng: number, lat: number, zoom = 5)
  {
    this.map?.setCenter([lng, lat]);
    this.map?.setZoom(zoom);
  }

  setView(view: MapViewState)
  {
    this.map?.jumpTo({
      center: view.center,
      zoom: view.zoom,
      pitch: view.pitch || 0,
      bearing: view.bearing || 0,
    });
  }

  getView(): MapViewState
  {
    const c = this.map!.getCenter();
    return {
      center: [c.lng, c.lat],
      zoom: this.map!.getZoom(),
      pitch: this.map!.getPitch(),
      bearing: this.map!.getBearing(),
    };
  }

  getMapInstance()
  {
    return this.map;
  }

  destroy()
  {
    this.map?.remove();
  }

  resize()
  {
    this.map?.resize();
  }

  // ── layer sync ─────────────────────────────────────────────────────────────
  async syncLayers(mapLayers: MapLayer[])
  {
    if (!this.map || !this.addedIds) return;

    const currentIds = new Set(mapLayers.map((l) => l.id));
    // @ts-ignore
    for (const id of [...this.addedIds])
    {
      if (!currentIds.has(id))
      {
        this.removeLayer(id);
        this.addedIds.delete(id);
      }
    }

    const sorted = [...mapLayers].sort((a, b) => a.zIndex - b.zIndex);

    for (const cfg of sorted)
    {
      const lid = `${PREFIX_MAPLIBRE_LAYER}${cfg.id}`;
      const isGeo = cfg.type === "geojson" || cfg.type === "wfs";

      if (!this.addedIds.has(cfg.id))
      {
        await this.addLayer(cfg);
      } else if (this.urlCache.get(cfg.id) !== cfg.url)
      {
        await this.updateLayerUrl(cfg);
      }

      // visibility / opacity update
      const vis = cfg.visibility ? "visible" : "none";
      try
      {
        if (isGeo)
        {
          for (const sfx of ["-fill", "-line", "-point"])
          {
            if (this.map.getLayer(`${lid}${sfx}`)) this.map.setLayoutProperty(`${lid}${sfx}`, "visibility", vis);
          }
        } else
        {
          if (this.map.getLayer(lid))
          {
            this.map.setLayoutProperty(lid, "visibility", vis);
            this.map.setPaintProperty(lid, "raster-opacity", cfg.opacity);
          }
        }
      } catch (e)
      {
        console.warn("[ML] update failed:", e);
      }
    }
  }

  async addLayer(cfg: MapLayer)
  {
    // Insert below the AOI layers so the search polygon (AOI) is never covered
    // by image/footprint overlays. Falls back to top-of-stack when no AOI present.
    const aoiLayerId = this.map
      ?.getStyle?.()
      ?.layers?.find(
        (l: any) => typeof l?.id === "string" && l.id.startsWith(AOI_LAYER_ID),
      )?.id;
    const before = MAPLIBRE_BEFORE_ID || aoiLayerId || undefined;
    const sid = `${PREFIX_MAPLIBRE_SOURCE}${cfg.id}`;
    if (!this.map || !this.addedIds || !cfg) return;
    try
    {
      const { layers, source } = await createMapLibreLayer(cfg);
      if (!!source) this.map.addSource(sid, source as maplibregl.SourceSpecification);
      if (!!layers?.length)
      {
        layers.forEach((layer) =>
        {
          if (!this.map) return;
          this.map.addLayer(layer as maplibregl.LayerSpecification, before);
        });
      }
      this.addedIds.add(cfg.id);
      this.urlCache.set(cfg.id, cfg.url ?? "");
    } catch (e)
    {
      console.warn("[ML] addLayer failed:", e);
    }
  }

  private async updateLayerUrl(cfg: MapLayer)
  {
    if (!this.map) return;
    const sid = `${PREFIX_MAPLIBRE_SOURCE}${cfg.id}`;
    const source = this.map.getSource(sid);
    if (!source) return;

    try
    {
      switch (cfg.type)
      {
        case "osm":
        case "xyz":
          (source as maplibregl.RasterTileSource).setTiles(expandUrl(cfg.url ?? ""));
          break;
        case "wms":
        case "wmts":
        case "cog":
          // URL construction is complex — remove and re-add
          this.removeLayer(cfg.id);
          this.addedIds?.delete(cfg.id);
          this.urlCache.delete(cfg.id);
          await this.addLayer(cfg);
          return;
        case "image": {
          const url =
            cfg?.imageParams?.urlType === "base64"
              ? cfg?.url?.startsWith("data:image")
                ? cfg.url
                : `data:image/png;base64,${cfg.url}`
              : (cfg.url ?? "");
          (source as maplibregl.ImageSource).updateImage({
            url,
            coordinates: (source as any).coordinates,
          });
          break;
        }
      }
    } catch (e)
    {
      console.warn("[ML] updateLayerUrl failed:", e);
    }

    this.urlCache.set(cfg.id, cfg.url ?? "");
  }

  removeLayer(id: string)
  {
    if (!this.map || !id) return;
    for (const sfx of ["-fill", "-line", "-point", ""])
    {
      try
      {
        if (this.map.getLayer(`${PREFIX_MAPLIBRE_LAYER}${id}${sfx}`)) this.map.removeLayer(`${PREFIX_MAPLIBRE_LAYER}${id}${sfx}`);
      } catch { }
    }
    try
    {
      if (this.map.getSource(`${PREFIX_MAPLIBRE_SOURCE}${id}`)) this.map.removeSource(`${PREFIX_MAPLIBRE_SOURCE}${id}`);
    } catch { }
  }

  updateGeoJSONSource(id: string, data: any)
  {
    if (!this.map) return;
    const sid = `${PREFIX_MAPLIBRE_SOURCE}${id}`;
    const source = this.map.getSource(sid) as maplibregl.GeoJSONSource;

    if (source)
    {
      source.setData(data);
    }
  }
}
